import type { FunctionalComponent } from 'vue'
import { popModal, pushModal } from '../store'
import { GridRow } from 'components/grid'
import Button from 'components/button'
import { isString } from '@/utils/is'
import { ref } from 'vue'
import { tv, type VariantProps } from 'tailwind-variants'

const contentClass = tv({
  base: 'text-center h-fit',
  variants: {
    padding: {
      default: 'p-8',
      large: 'py-[71px] px-[51px]',
      medium: 'p-6',
      small: 'p-4',
    }
  },
  defaultVariants: {
    padding: 'default'
  }
})

const innerClass = tv({
  base: 'flex flex-col items-center',
  variants: {
    gap: {
      none: 'gap-0',
      small: 'gap-1',
      medium: 'gap-2',
      large: 'gap-4',
    }
  },
  defaultVariants: {
    gap: 'small'
  }
})

export type ShowPromptOptions = {
  title?: string
  description?: string
  body?: FunctionalComponent<any> | string
  answerYesText?: string
  answerYesVariant?: string,
  answerNoText?: string
  answerNoVariant?: string
  contentClass?: VariantProps<typeof contentClass>
  innerClass?: VariantProps<typeof innerClass>
  // New input field options
  inputLabel?: string
  inputPlaceholder?: string
  inputType?: string
  inputRequired?: boolean
  defaultValue?: string
  closeOnClickOutside?: boolean
}

export type PromptResult = boolean | null | {
  confirmed: boolean
  inputValue?: string
}

export const showPrompt = (options: ShowPromptOptions): Promise<PromptResult> => {
  const inputValue = ref(options.defaultValue || '')

  return new Promise((resolve) => {
    let resolved = false

    const resolvePromise = (result: PromptResult) => {
      if (resolved) return
      resolved = true
      resolve(result)
    }

    const handleClickYes = async () => {
      if (options.inputRequired && !inputValue.value) {
        return
      }
      // Return either a boolean or an object with inputValue based on whether input is used
      if (options.inputLabel) {
        resolvePromise({
          confirmed: true,
          inputValue: inputValue.value
        })
      } else {
        resolvePromise(true)
      }
      await popModal()
    }

    const handleClickNo = async () => {
      // Similarly for cancellation
      if (options.inputLabel) {
        resolvePromise({
          confirmed: false,
          inputValue: undefined
        })
      } else {
        resolvePromise(false)
      }
      await popModal()
    }

    pushModal({
      title: options.title,
      style: {
        maxWidth: 'clamp(520px, 40vw, 720px)',
        minWidth: 'auto',
        minHeight: '300px',
        height: 'fit-content',
      },
      contentStyle: {
        display: 'grid',
        justifyContent: 'center',
        alignItems: 'center'
      },
      disableDefaultContentStyles: true,
      closeOnClickOutside: options.closeOnClickOutside ?? false,
      onClose: () => {
        // When modal is closed via click outside or X button, return null to indicate dismissal without action
        resolvePromise(null)
      },
      body: () => {
        return (
          <div
            class="flex w-full justify-center overflow-x-hidden"
          >
            <div class="flex flex-col items-center gap-1 text-center p-8 w-full max-w-full"
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {options.body ? isString(options.body) ? options.body : <options.body /> : ''}
              {options.description && <span class="text-gray-500 mt-2 text-center">{options.description}</span>}
            </div>

            {/* Input field, only shown if inputLabel is provided */}
            {options.inputLabel && (
              <div class="mt-4 text-left w-full">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {options.inputLabel}
                  {options.inputRequired && <span class="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type={options.inputType || 'text'}
                  placeholder={options.inputPlaceholder || ''}
                  v-model={inputValue.value}
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
          </div>
        )
      },
      actions: () => {
        return (
          <GridRow
            layout="max-content max-content"
            gap="1rem"
            style={{ justifyContent: 'center', width: '100%' }}
          >
            <Button variant={options.answerNoVariant ? options.answerNoVariant : 'transparent'} outlined onClick={handleClickNo}>
              {options.answerNoText || 'No'}
            </Button>
            <Button
              variant={options.answerYesVariant ? options.answerYesVariant : 'error'}
              onClick={handleClickYes}
              disabled={options.inputRequired && !inputValue.value}
            >
              {options.answerYesText || 'Yes'}
            </Button>
          </GridRow>
        )
      }
    })
  })
}
